import { BIG_ARENAS } from './layouts-big';
import { ROUTES } from './routes';
import type { ArenaLayout } from './layout-types';

export type { ArenaLayout, ObstacleDef, RoomKind } from './layout-types';
export { roomKindOf } from './layout-types';

/**
 * Room layouts — geometry only (the look comes from the sector theme,
 * config/themes.ts). Coordinates are fractions of the arena size. New
 * layouts are APPENDED: saves index this list (`arenaIndex`).
 * Classic arenas first, then the big fields, then ADVANCE routes.
 */
const CLASSIC: ReadonlyArray<ArenaLayout> = [
  {
    id: 'void-open',
    nameKey: 'arena.void-open',
    obstacles: [],
  },
  {
    id: 'nebula-pillars',
    nameKey: 'arena.nebula-pillars',
    obstacles: [
      { x: 0.3, y: 0.3, radius: 90 },
      { x: 0.7, y: 0.3, radius: 90 },
      { x: 0.3, y: 0.7, radius: 90 },
      { x: 0.7, y: 0.7, radius: 90 },
    ],
  },
  {
    id: 'forest-corridor',
    nameKey: 'arena.forest-corridor',
    obstacles: [
      // Horizontal center wall (three pieces) leaving top/bottom lanes
      { x: 0.38, y: 0.5, radius: 80 },
      { x: 0.5, y: 0.5, radius: 80 },
      { x: 0.62, y: 0.5, radius: 80 },
      { x: 0.15, y: 0.2, radius: 60 },
      { x: 0.85, y: 0.8, radius: 60 },
    ],
  },
  {
    id: 'void-corridor',
    nameKey: 'arena.void-corridor',
    width: 3000,
    height: 980, // claustrophobic: vertical dodging room is scarce
    obstacles: [],
    // The corridor is a shooting gallery — in BOTH directions (M9)
    turrets: [{ x: 0.45, y: 0.18 }, { x: 0.62, y: 0.82 }],
  },
  {
    id: 'nebula-expanse',
    nameKey: 'arena.nebula-expanse',
    width: 3400,
    height: 2300, // wide open: distance is the resource, magnet matters
    obstacles: [
      { x: 0.5, y: 0.5, radius: 110 },
      { x: 0.2, y: 0.75, radius: 70 },
      { x: 0.8, y: 0.25, radius: 70 },
    ],
  },
  {
    id: 'forest-chasm',
    nameKey: 'arena.forest-chasm',
    obstacles: [
      // Chasm across the middle with two bridges; bullets cross, ships don't
      { x: 0.14, y: 0.5, radius: 105, pit: true },
      { x: 0.27, y: 0.5, radius: 105, pit: true },
      // bridge gap at ~0.4
      { x: 0.53, y: 0.5, radius: 105, pit: true },
      { x: 0.66, y: 0.5, radius: 105, pit: true },
      // bridge gap at ~0.79
      { x: 0.92, y: 0.5, radius: 105, pit: true },
      { x: 0.35, y: 0.14, radius: 70, pit: true },
      { x: 0.7, y: 0.86, radius: 70, pit: true },
    ],
  },
  {
    id: 'magma-cauldron',
    nameKey: 'arena.magma-cauldron',
    obstacles: [
      // Boiling center cluster — flying over it burns (docs/12 E4)
      { x: 0.44, y: 0.42, radius: 115, lava: true },
      { x: 0.58, y: 0.55, radius: 95, lava: true },
      { x: 0.47, y: 0.66, radius: 70, lava: true },
      { x: 0.18, y: 0.22, radius: 65, lava: true },
      { x: 0.84, y: 0.78, radius: 65, lava: true },
      { x: 0.16, y: 0.8, radius: 60 },
      { x: 0.85, y: 0.18, radius: 60 },
    ],
  },
  {
    id: 'void-ring',
    nameKey: 'arena.void-ring',
    width: 2800,
    height: 2000,
    obstacles: [
      // Rock ring around the center — orbit fights with spokes to dodge
      { x: 0.5, y: 0.22, radius: 80 },
      { x: 0.71, y: 0.32, radius: 80 },
      { x: 0.78, y: 0.55, radius: 80 },
      { x: 0.64, y: 0.76, radius: 80 },
      { x: 0.36, y: 0.76, radius: 80 },
      { x: 0.22, y: 0.55, radius: 80 },
      { x: 0.29, y: 0.32, radius: 80 },
    ],
  },
  {
    id: 'abyss-trench',
    nameKey: 'arena.abyss-trench',
    width: 3000,
    height: 1150,
    obstacles: [
      // A drowned trench: pits split the floor into two long lanes
      { x: 0.3, y: 0.5, radius: 95, pit: true },
      { x: 0.45, y: 0.5, radius: 95, pit: true },
      { x: 0.6, y: 0.5, radius: 95, pit: true },
      { x: 0.75, y: 0.5, radius: 95, pit: true },
    ],
    // Each lane is watched from across the trench (M9)
    turrets: [{ x: 0.52, y: 0.16 }, { x: 0.52, y: 0.84 }],
  },
  {
    id: 'abyss-vortex',
    nameKey: 'arena.abyss-vortex',
    obstacles: [
      // Sinkhole ring around the center — fight orbits the drop
      { x: 0.5, y: 0.26, radius: 85, pit: true },
      { x: 0.68, y: 0.36, radius: 85, pit: true },
      { x: 0.68, y: 0.64, radius: 85, pit: true },
      { x: 0.5, y: 0.74, radius: 85, pit: true },
      { x: 0.32, y: 0.64, radius: 85, pit: true },
      { x: 0.32, y: 0.36, radius: 85, pit: true },
    ],
  },
  {
    id: 'magma-crossfire',
    nameKey: 'arena.magma-crossfire',
    obstacles: [
      // Lava at the diagonals + a center rock: safe lanes form a cross
      { x: 0.32, y: 0.3, radius: 110, lava: true },
      { x: 0.68, y: 0.3, radius: 110, lava: true },
      { x: 0.32, y: 0.7, radius: 110, lava: true },
      { x: 0.68, y: 0.7, radius: 110, lava: true },
      { x: 0.5, y: 0.5, radius: 70 },
    ],
  },
  {
    id: 'nebula-gauntlet',
    nameKey: 'arena.nebula-gauntlet',
    width: 3100,
    height: 1300,
    obstacles: [
      // Staggered pillars: a slalom — charge bosses get VERY spicy here
      { x: 0.25, y: 0.34, radius: 85 },
      { x: 0.4, y: 0.66, radius: 85 },
      { x: 0.55, y: 0.34, radius: 85 },
      { x: 0.7, y: 0.66, radius: 85 },
      { x: 0.85, y: 0.34, radius: 85 },
    ],
    // Turrets tucked behind the pillars cover the slalom lanes (M9)
    turrets: [{ x: 0.4, y: 0.34 }, { x: 0.7, y: 0.34 }],
  },
  {
    id: 'void-fracture',
    nameKey: 'arena.void-fracture',
    obstacles: [
      // Shattered floor: scattered cracks bullets cross but ships cannot
      { x: 0.4, y: 0.25, radius: 75, pit: true },
      { x: 0.62, y: 0.42, radius: 90, pit: true },
      { x: 0.35, y: 0.62, radius: 80, pit: true },
      { x: 0.58, y: 0.78, radius: 70, pit: true },
      { x: 0.8, y: 0.6, radius: 65, pit: true },
    ],
  },
  {
    id: 'forest-grove',
    nameKey: 'arena.forest-grove',
    obstacles: [
      // Old-growth grove: lots of small cover, no clean firing lines
      { x: 0.28, y: 0.28, radius: 55 },
      { x: 0.5, y: 0.22, radius: 50 },
      { x: 0.72, y: 0.3, radius: 55 },
      { x: 0.38, y: 0.52, radius: 50 },
      { x: 0.64, y: 0.55, radius: 55 },
      { x: 0.27, y: 0.74, radius: 50 },
      { x: 0.52, y: 0.78, radius: 55 },
      { x: 0.76, y: 0.74, radius: 50 },
    ],
  },
];

export const LAYOUTS: ReadonlyArray<ArenaLayout> = [...CLASSIC, ...BIG_ARENAS, ...ROUTES];
